import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'
import type { CurrencyAmount, ExpressionType } from '@/services/stats/statsApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

/**
 * One floor price that applies to a line item's bids. A plain one has just a
 * price; the others raise the floor only for bids that use a kind of
 * targeting, or for one creative size. From `AdvertiserSummaryResponse` in core.
 */
export interface FloorPrice {
  price: CurrencyAmount
  targetingType?: ExpressionType
  /** Width and height of the creative size the price is for. */
  size?: [number, number]
}

export interface FloorPriceAdUnit {
  id: string
  name: string
  description: string
  /** Every size the ad unit accepts, as width and height. */
  dimensions: [number, number][]
  floorPrices: FloorPrice[]
}

export interface FloorPriceSite {
  id: string
  name: string
  description: string
  siteUrl?: string
  floorPrices: FloorPrice[]
  adUnits: FloorPriceAdUnit[]
}

export interface FloorPricePublisher {
  id: string
  name: string
  description: string
  floorPrices: FloorPrice[]
  sites: FloorPriceSite[]
}

export interface ObjectRef {
  id: string
  /** Missing when the user cannot see the object. */
  name?: string
}

/** One bid range the optimiser set for a site or ad unit. */
export interface BidUpdate {
  time: string
  lowerCpm?: CurrencyAmount
  upperCpm?: CurrencyAmount
  /** Set when the update was for one ad unit rather than the whole site. */
  adUnit: ObjectRef | null
}

/** A dynamic-bidding line item's bid updates on one site, oldest first. */
export interface SiteBidHistory {
  site: ObjectRef
  updates: BidUpdate[]
}

type Json = Record<string, unknown>

function asList(v: unknown): Json[] {
  return Array.isArray(v) ? (v as Json[]) : []
}

function mapRef(v: unknown): ObjectRef | null {
  if (!v || typeof v !== 'object') return null
  const r = v as Json
  return { id: (r.id as string) ?? '', name: r.name as string | undefined }
}

function mapFloorPrice(r: Json): FloorPrice {
  // `sizes` is a ruleset size, `{ dimensions: [w, h] }`.
  const dims = (r.sizes as Json | undefined)?.dimensions
  return {
    price: r.price as CurrencyAmount,
    targetingType: r.targetingType as ExpressionType | undefined,
    size: Array.isArray(dims) && dims.length >= 2 ? [dims[0], dims[1]] : undefined,
  }
}

function mapAdUnit(r: Json): FloorPriceAdUnit {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    dimensions: asList(r.dimensions) as unknown as [number, number][],
    floorPrices: asList(r.floorPrices).map(mapFloorPrice),
  }
}

function mapSite(r: Json): FloorPriceSite {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    siteUrl: r.siteUrl as string | undefined,
    floorPrices: asList(r.floorPrices).map(mapFloorPrice),
    adUnits: asList(r.adUnits).map(mapAdUnit),
  }
}

function mapPublisher(r: Json): FloorPricePublisher {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    floorPrices: asList(r.floorPrices).map(mapFloorPrice),
    sites: asList(r.sites).map(mapSite),
  }
}

function mapBidUpdate(r: Json): BidUpdate {
  return {
    time: (r.time as string) ?? '',
    lowerCpm: r.lowerCpm as CurrencyAmount | undefined,
    upperCpm: r.upperCpm as CurrencyAmount | undefined,
    adUnit: mapRef(r.adUnit),
  }
}

export const LineItemDiagnosticsApi = {
  /**
   * The floor prices the line item's bids meet on each publisher, site and ad
   * unit it can deliver to — UII's "Floor prices" diagnostic.
   */
  getFloorPrices: async (lineItemId: string): Promise<FloorPricePublisher[]> => {
    const params = new URLSearchParams({ context: getContext(), lineItem: lineItemId })
    const res = await apiFetch(`${API_BASE}/publisherinfo/lineitem?${params}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch floor prices')
    const data = (await res.json()) as { publishers?: Json[] }
    return asList(data.publishers).map(mapPublisher)
  },

  /**
   * The bid ranges dynamic bidding has set for the line item, per site. The
   * backend keeps a short window, three hours by default.
   */
  getBidHistory: async (lineItemId: string): Promise<SiteBidHistory[]> => {
    const params = new URLSearchParams({ context: getContext() })
    const res = await apiFetch(`${API_BASE}/bidupdates/lineitem/${lineItemId}?${params}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch bid history')
    // No updates comes back as an empty body rather than an empty list.
    const text = await res.text()
    if (!text) return []
    return asList(JSON.parse(text)).flatMap((r) => {
      const site = mapRef(r.site)
      if (!site) return []
      const updates = asList(r.updates)
        .map(mapBidUpdate)
        .sort((a, b) => a.time.localeCompare(b.time))
      return [{ site, updates }]
    })
  },
}
